/**
 * NOTE FOR FUTURE MAINTAINERS:
 *
 * This component does not use `next/image` on purpose.
 * Instead, it manually builds <source> elements and `srcSet`s to support:
 *
 * - Custom widths/heights per responsive breakpoint
 * - Pixel density-specific images (1x, 2x, 3x) via `srcSet`
 * - CDN URL transformation: appends width, height, quality, and focal-point
 *   parameters to optimize delivery from the image processor
 *
 * If you're refactoring this, be aware that the logic is tightly coupled to:
 * 1. Tailwind's configured breakpoints (via `tailwind.config.js`)
 * 2. A custom image CDN endpoint set in `process.env.IMAGE_PROCESSOR_URL`
 *
 * The main benefit is full control over how images are served/resized per device.
 * Switching to `next/image` would lose some of this precision unless the CDN
 * is compatible with `next/image`'s loader config.
 */

import { useEffect, useRef, useMemo } from 'react';
import Head from 'next/head';
import PropTypes from 'prop-types';
import tailwindConfig from '../../tailwind.config.js';
import { loadLazyImage, getUrlExtension } from '@/utils';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { selectIsRobot } from '@/store/index';
import { Box } from '../index.js';

const pixelDensities = [1, 2, 3];

function getScreens() {
  const screens = [{ key: 'xs', value: tailwindConfig.theme.screens.sm }];
  Object.entries(tailwindConfig.theme.screens).forEach(([key, value]) => {
    if (typeof value !== 'object') {
      screens.push({ key, value });
    }
  });
  return screens;
}

function getImageSize(sizes, breakpoint) {
  if (!sizes) return null;
  if (sizes[breakpoint] > 0) return sizes[breakpoint];

  const keys = getScreens().map(function (s) {
    return s.key;
  });

  let index = keys.indexOf(breakpoint);
  while (index-- > 0) {
    const key = keys[index];
    if (sizes[key] > 0) return sizes[key];
  }

  return null;
}

function buildImageUrl(imageUrl, breakpoint, format, pixelDensity, widths, heights, cdnProps) {
  const baseUrl = process.env.IMAGE_PROCESSOR_URL?.replace(/\/+$/, '') || '';
  const normalizedSrc = imageUrl.replace(/^\/+/, '');
  const url = new URL(`${baseUrl}/${normalizedSrc}`);

  const width = getImageSize(widths, breakpoint);
  const height = getImageSize(heights, breakpoint);

  if (width) url.searchParams.set('width', Math.round(width * pixelDensity));
  if (height) url.searchParams.set('height', Math.round(height * pixelDensity));
  if (format) url.searchParams.set('format', format);

  if (cdnProps) {
    Object.entries(cdnProps).forEach(function ([k, v]) {
      url.searchParams.set(k, v);
    });
  }

  return url.toString() + (pixelDensity ? ` ${pixelDensity}x` : '');
}

function buildSrcSet(image, breakpoint, format, widths, heights, cdnProps) {
  return pixelDensities.map(function (pd) {
    return buildImageUrl(image.imageUrl, breakpoint, format, pd, widths, heights, cdnProps);
  });
}

function getMediaQuery(screens, i) {
  const current = screens[i];
  const next = screens[i + 1];
  if (i === 0) return `(max-width: ${parseInt(current.value) - 1}px)`;
  if (!next) return `(min-width: ${current.value})`;
  return `(min-width: ${current.value}) and (max-width: ${parseInt(next.value) - 1}px)`;
}

function ResponsiveImage({
  animate,
  motion,
  image,
  widths,
  heights,
  preload,
  cdnProps,
  dataNotLazy,
  imgProps = {},
  className,
  ...props
}) {
  const { className: imageClassName, ..._imgProps } = imgProps;
  const pictureRef = useRef();
  const isRobot = useSelector(selectIsRobot);

  const screens = useMemo(function () {
    return getScreens();
  }, []);

  useEffect(
    function () {
      const ref = pictureRef.current;
      if (isRobot || !ref) return;

      function loadIfInView() {
        const { top } = ref.getBoundingClientRect();
        if (top < window.innerHeight) {
          loadLazyImage(ref);
          return true;
        }
        return false;
      }

      if (loadIfInView()) return;

      const observer = new IntersectionObserver(function ([entry]) {
        if (entry.isIntersecting) {
          loadLazyImage(entry.target);
          observer.unobserve(ref);
        }
      });

      observer.observe(ref);
      return function () {
        if (ref) observer.unobserve(ref);
      };
    },
    [isRobot],
  );

  if (!image) return null;

  const isSvg = getUrlExtension(image.imageUrl) === 'svg';

  const altText = image.imageAlt?.slice(0, 100) || image.caption?.slice(0, 100) || `image-${image.mediaId}`;

  const sources = screens.flatMap(function (screen, i) {
    const media = getMediaQuery(screens, i);
    const sizes = pixelDensities
      .map(function (pd) {
        return `${pd}x`;
      })
      .join(', ');

    const srcSetWebp = buildSrcSet(image, screen.key, 'webp', widths, heights, cdnProps);
    const srcSetDefault = buildSrcSet(image, screen.key, null, widths, heights, cdnProps);

    return [
      !isSvg && (
        <source
          key={`${image.imageUrl}-${i}-webp-${screen.key}`}
          sizes={sizes}
          media={media}
          type="image/webp"
          {...(preload || dataNotLazy ? { srcSet: srcSetWebp.join(', ') } : { 'data-srcset': srcSetWebp.join(', ') })}
        />
      ),
      <source
        key={`img-${image.imageUrl}-${i}-${screen.key}`}
        sizes={sizes}
        media={media}
        {...(preload || dataNotLazy
          ? { srcSet: srcSetDefault.join(', ') }
          : { 'data-srcset': srcSetDefault.join(', ') })}
      />,
    ];
  });

  const preloadLinks = preload
    ? screens.map(function (screen, i) {
        return (
          <link
            key={`preload-${image.imageUrl}-${i}`}
            rel="preload"
            as="image"
            fetchpriority="high"
            href={buildImageUrl(image.imageUrl, 'xs', 'webp', 1, widths, heights, cdnProps)}
            imageSrcSet={buildSrcSet(image, screen.key, 'webp', widths, heights, cdnProps).join(', ')}
            media={getMediaQuery(screens, i)}
          />
        );
      })
    : null;

  const imgAttrs =
    preload || dataNotLazy
      ? { src: buildImageUrl(image.imageUrl, 'lg', null, 1, widths, heights, cdnProps) }
      : {
          'data-src': buildImageUrl(image.imageUrl, 'lg', null, 1, widths, heights, cdnProps),
          src: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAJcEhZcwAAHYcAAB2HAY/l8WUAAAAbSURBVChTYxRT0fvPQARgQhfABUYV4gVEKwQAS84Be2GueHAAAAAASUVORK5CYII=',
        };

  const pictureElement = (
    <picture
      key={image.mediaId}
      className={classNames('h-full w-full', className, !preload && !dataNotLazy && 'lazy-image')}
      data-not-lazy={dataNotLazy}
      ref={pictureRef}
      {...props}
    >
      {sources}
      <img
        alt={altText}
        className={classNames('block h-full w-full object-cover', imageClassName)}
        {...imgAttrs}
        {..._imgProps}
      />
    </picture>
  );

  return (
    <>
      <Head>{preloadLinks}</Head>
      {!animate && !motion ? (
        pictureElement
      ) : (
        <Box motion={motion} className="h-full w-full">
          {pictureElement}
        </Box>
      )}
    </>
  );
}

ResponsiveImage.propTypes = {
  image: PropTypes.object,
  widths: PropTypes.object,
  heights: PropTypes.object,
  preload: PropTypes.bool,
  cdnProps: PropTypes.object,
  dataNotLazy: PropTypes.bool,
  imgProps: PropTypes.object,
  className: PropTypes.string,
  animate: PropTypes.bool,
  motion: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default ResponsiveImage;
